import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { ProgressBackdrop } from 'ui/src/components';
import { GridArea } from '../../components/common/GridArea';
import { useRecoilValue } from 'recoil';
import globalState from 'ui/src/state/globalState';
import AdminApi from '../../api/AdminApi';
import { GridOptions } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import 'react-datepicker/dist/react-datepicker.css';
import ReactDatePicker from 'react-datepicker';

const maskName = (name) => {
  if (!name || name === '-') return name;
  if (name.length <= 2) {
    return `${name[0]}*`;
  }
  return name[0] + '*'.repeat(name.length - 2) + name[name.length - 1];
};

const LoginListGridDefs: GridOptions = {
  columnDefs: [
    { headerName: '일렬번호', field: 'log_seq', flex: 1, minWidth: 120, cellStyle: { textAlign: 'center' } },
    {
      headerName: '성명',
      field: 'user_nm',
      flex: 1,
      minWidth: 100,
      cellStyle: { textAlign: 'center' },
      valueFormatter: (params) => maskName(params.value),
    },
    {
      headerName: '아이디',
      field: 'user_id',
      flex: 1,
      minWidth: 100,
      cellStyle: { textAlign: 'center' },
    },
    {
      headerName: '소속',
      field: 'dept_nm',
      flex: 2,
      minWidth: 200,
      cellStyle: { textAlign: 'left' },
    },
    {
      headerName: '직위명',
      field: 'jbps_fm',
      flex: 1,
      minWidth: 120,
      cellStyle: { textAlign: 'center' },
    },
    {
      headerName: '접속일시',
      field: 'cntn_dt_nm',
      flex: 1,
      minWidth: 130,
      cellStyle: { textAlign: 'center' },
    },
  ],
};

// yyyyMMdd 포맷 변환 유틸
const toYYYYMMDD = (v?: string) => {
  if (!v) return '';
  const digits = v.replace(/[^0-9]/g, '');
  if (digits.length >= 8) return digits.slice(0, 8);
  const d = new Date(v);
  if (!isNaN(d.getTime())) {
    const yyyy = String(d.getFullYear());
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');

    return `${yyyy}${mm}${dd}`;
  }

  return '';
};

const LoginListPage = () => {
  const [rowData, setRowData] = useState([]);
  const gridRef = useRef<AgGridReact>(null);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [searchParams, setSearchParams] = useState<any>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<string>(''); // 선택된 기간 버튼 상태

  const { register, handleSubmit, setValue, control, getValues } = useForm();
  const { UserInformation } = useRecoilValue(globalState);
  const admin = UserInformation.userId;

  useEffect(() => {
    fetchData();
  }, []);

  // page가 변경될 때마다 데이터를 새로 가져오도록 useEffect 추가
  useEffect(() => {
    fetchData(searchParams);
  }, [page]);

  const fetchData = async (params?: any) => {
    try {
      setLoading(true);

      // 총 개수 조회
      const countParams = {
        searchUserNm: params?.searchUserNm,
        searchUserId: params?.searchUserId,
        searchStartDt: toYYYYMMDD(params?.searchStartDt),
        searchEndDt: toYYYYMMDD(params?.searchEndDt),
      };

      const countResult = await AdminApi.getAccessLogsCount(countParams);
      const totalCount = countResult.data || 0;
      setTotal(totalCount);

      // 접속 로그 리스트 조회
      const listParams = {
        pageSize: 10,
        pageNo: page,
        searchUserNm: params?.searchUserNm,
        searchUserId: params?.searchUserId,
        searchStartDt: toYYYYMMDD(params?.searchStartDt),
        searchEndDt: toYYYYMMDD(params?.searchEndDt),
      };

      const listResult = await AdminApi.getAccessLogs(listParams);
      const logs = listResult.data || [];

      if (logs.length < 1) {
        setRowData([]);
        return;
      }

      // API 응답 데이터를 테이블 형식에 맞게 변환
      const formattedData = logs.map((log) => {
        // 소속 정보를 조합 (ctpv_nm + sgg_nm + dept_nm )
        const affiliation = [log.ctpv_nm, log.sgg_nm, log.dept_nm]
          .filter((item) => item && item.trim() !== '') // 빈 값 제외
          .join(' '); // 공백으로 구분

        return {
          log_seq: log.log_seq || '',
          user_nm: log.user_nm || '',
          user_id: log.user_id || '',
          dept_nm: affiliation,
          sgg_nm: log.sgg_nm || '',
          ctpv_nm: log.ctpv_nm || '',
          cntn_dt_nm: log.cntn_dt_nm || (log.cntn_dt ? log.cntn_dt.substring(0, 10) : ''),
          jbps_fm: log.jbps_fm || '',
        };
      });

      console.log('Fetched access logs:', formattedData);
      setRowData(formattedData);
    } catch (e) {
      console.error('API 호출 실패:', e);
      setTotal(0);
      setRowData([]);
    } finally {
      setLoading(false);
    }
  };

  const excelDownload = async () => {
    try {
      // 현재 폼의 값 또는 최근 검색 상태를 사용
      const formValues = getValues();
      const params = {
        searchUserNm: searchParams?.searchUserNm ?? formValues?.searchUserNm ?? '',
        searchUserId: searchParams?.searchUserId ?? formValues?.searchUserId ?? '',
        searchStartDt: toYYYYMMDD(searchParams?.searchStartDt ?? formValues?.searchStartDt ?? ''),
        searchEndDt: toYYYYMMDD(searchParams?.searchEndDt ?? formValues?.searchEndDt ?? ''),
      };

      const res = await AdminApi.downloadAccessLogsExcel(params);
      const blob = new Blob([res.data], { type: res.headers['content-type'] || 'application/vnd.ms-excel' });

      // 파일명 추출 (Content-Disposition)
      const disposition = res.headers['content-disposition'] || res.headers['Content-Disposition'];
      let filename = 'access_logs.xlsx';
      if (disposition) {
        const match = /filename\*=UTF-8''(?<utf8>[^;]+)|filename="?(?<plain>[^;"]+)"?/i.exec(disposition);
        const picked = match?.groups?.utf8 || match?.groups?.plain || '';
        const rawName = decodeURIComponent(picked.trim());
        if (rawName) filename = rawName;
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('엑셀 다운로드 실패:', e);
      alert('엑셀 다운로드 중 오류가 발생했습니다.');
    }
  };

  const deselect = useCallback(() => {
    setRowData((prev) => prev.map((i) => ({ ...i, selected: false })));
  }, []);

  useEffect(() => {
    console.log(rowData);
  }, [rowData]);

  const onFirstDataRendered = (params) => {
    params.api.sizeColumnsToFit();
  };

  const onSubmit = (formData) => {
    console.log('Search params:', formData);
    setSearchParams(formData); // 검색 파라미터 저장
    setPage(0); // 검색 시 페이지를 0으로 초기화
    fetchData(formData);
  };

  // 기간 버튼 클릭 시 날짜 범위 설정 함수
  const handlePeriodClick = (period: string) => {
    const today = new Date();
    const formatDate = (date: Date) => {
      return date.toISOString().split('T')[0];
    };

    let startDate: Date;
    let endDate = today;

    switch (period) {
      case '1week':
        startDate = new Date(today);
        startDate.setDate(today.getDate() - 7);
        break;
      case '2weeks':
        startDate = new Date(today);
        startDate.setDate(today.getDate() - 14);
        break;
      case '1month':
        startDate = new Date(today);
        startDate.setMonth(today.getMonth() - 1);
        break;
      case '3months':
        startDate = new Date(today);
        startDate.setMonth(today.getMonth() - 3);
        break;
      case 'lastMonth': {
        // 지난달 1일부터 지난달 마지막일까지
        const lastMonth = today.getMonth() === 0 ? 11 : today.getMonth() - 1;
        const lastMonthYear = today.getMonth() === 0 ? today.getFullYear() - 1 : today.getFullYear();

        startDate = new Date(lastMonthYear, lastMonth, 2); // 1일 -> 2일로 변경
        endDate = new Date(lastMonthYear, lastMonth + 1, 1); // 마지막일 -> 마지막일+1로 변경
        break;
      }
      default:
        startDate = today;
    }

    const formattedStartDate = formatDate(startDate);
    const formattedEndDate = formatDate(endDate);

    console.log('Setting dates:', { start: formattedStartDate, end: formattedEndDate });

    // setValue를 개별적으로 호출하고 shouldValidate와 shouldDirty 옵션 추가
    setValue('searchStartDt', formattedStartDate, { shouldValidate: true, shouldDirty: true });
    setValue('searchEndDt', formattedEndDate, { shouldValidate: true, shouldDirty: true });

    setSelectedPeriod(period); // 선택된 기간 상태 업데이트

    // 강제로 DOM 요소에도 값 설정 (fallback)
    const startInput = document.getElementById('searchStartDt') as HTMLInputElement;
    const endInput = document.getElementById('searchEndDt') as HTMLInputElement;

    if (startInput) startInput.value = formattedStartDate;
    if (endInput) endInput.value = formattedEndDate;
  };

  useEffect(() => {
    const handlePointerDown = (e: MouseEvent) => {
      const active = document.activeElement as HTMLElement | null;
      if (!active) return;
      const isDateInput = active.tagName === 'INPUT' && (active as HTMLInputElement).type === 'date';
      if (!isDateInput) return;

      const target = e.target as HTMLElement;
      // date 인풋을 누른 경우는 유지, 그 외에는 blur 시도
      if (target.closest('#searchStartDt') || target.closest('#searchEndDt')) return;

      (active as HTMLInputElement).blur();
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      const active = document.activeElement as HTMLElement | null;
      if (active && active.tagName === 'INPUT' && (active as HTMLInputElement).type === 'date') {
        (active as HTMLInputElement).blur();
      }
    };

    // 캡처 단계에서 먼저 blur 처리
    document.addEventListener('pointerdown', handlePointerDown, true);
    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, []);

  return (
    <section className="cPage">
      <article className="pageTitArea">
        <div className="pageTitLeft">
          <div
            className="pageTit"
            style={{ marginTop: '16px' }}
          >
            로그인 정보 조회
          </div>
        </div>
      </article>

      <article className="pageConArea">
        <div
          className="pageSearchArea"
          style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}
        >
          {/* 왼쪽 블록: 성명 + 아이디 */}

          <div
            className="pageSearchItem pageSearchFirst"
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <label
              className="searchLabel"
              htmlFor="searchUserNm"
            >
              성명
            </label>
            <input
              id="searchUserNm"
              type="text"
              maxLength={10}
              {...register('searchUserNm')}
            />
          </div>
          <div
            className="pageSearchItem custom"
            style={{ display: 'flex', alignItems: 'center' }}
          >
            <label
              className="searchLabel"
              htmlFor="searchUserId"
            >
              아이디
            </label>
            <input
              id="searchUserId"
              type="text"
              {...register('searchUserId')}
              maxLength={30}
            />
          </div>

          {/* 오른쪽 블록: 기간 + 검색버튼 (둘 사이 줄바꿈 금지) */}
          <div
            className="searchGroup-right"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              flexWrap: 'nowrap',
              flex: '1 1 auto',
            }}
          >
            <div
              className="pageSearchItem"
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <label
                className="searchLabel"
                htmlFor="dateinput"
              >
                기간
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Controller
                    name="searchStartDt"
                    control={control}
                    render={({ field }) => (
                      <ReactDatePicker
                        selected={field.value ? new Date(field.value) : null}
                        onChange={(date: Date | null) => {
                          field.onChange(
                            date
                              ? new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().split('T')[0]
                              : '',
                          );
                        }}
                        dateFormat="yyyy-MM-dd"
                        placeholderText="시작일"
                        className="react-datepicker-input"
                        isClearable
                      />
                    )}
                  />
                  <span>~</span>
                  <Controller
                    name="searchEndDt"
                    control={control}
                    render={({ field }) => (
                      <ReactDatePicker
                        selected={field.value ? new Date(field.value) : null}
                        onChange={(date: Date | null) => {
                          field.onChange(
                            date
                              ? new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().split('T')[0]
                              : '',
                          );
                        }}
                        dateFormat="yyyy-MM-dd"
                        placeholderText="종료일"
                        className="react-datepicker-input"
                        isClearable
                      />
                    )}
                  />
                </div>
                <div style={{ display: 'flex', gap: '0' }}>
                  <button
                    type="button"
                    onClick={() => handlePeriodClick('1week')}
                    className={`dateRangeBtn ${selectedPeriod === '1week' ? 'active' : ''}`}
                  >
                    1주일
                  </button>
                  <button
                    type="button"
                    onClick={() => handlePeriodClick('2weeks')}
                    className={`dateRangeBtn ${selectedPeriod === '2weeks' ? 'active' : ''}`}
                  >
                    2주일
                  </button>
                  <button
                    type="button"
                    onClick={() => handlePeriodClick('1month')}
                    className={`dateRangeBtn ${selectedPeriod === '1month' ? 'active' : ''}`}
                  >
                    1개월
                  </button>
                  <button
                    type="button"
                    onClick={() => handlePeriodClick('3months')}
                    className={`dateRangeBtn ${selectedPeriod === '3months' ? 'active' : ''}`}
                  >
                    3개월
                  </button>
                  <button
                    type="button"
                    onClick={() => handlePeriodClick('lastMonth')}
                    className={`dateRangeBtn ${selectedPeriod === 'lastMonth' ? 'active' : ''}`}
                  >
                    지난달
                  </button>
                </div>
              </div>
            </div>
            <div className="searchBtnWrap">
              <button
                type="button"
                className="bSearch"
                onClick={handleSubmit(onSubmit)}
              >
                <span>검색</span>
              </button>
            </div>
          </div>
        </div>
      </article>

      <article className="pageConArea">
        <div className="cTableWrap mt40">
          <div className="cTableTopArea">
            <div className="cTableTopLeft">
              <ul className="cTableTopData">
                <li>
                  총 <span className="num">{total}</span> 건
                </li>
              </ul>
            </div>
            <div className="cTableTopRight">
              <button
                type="button"
                className="bLineType05 bDownload"
                onClick={excelDownload}
              >
                <span>엑셀 다운로드</span>
              </button>
            </div>
          </div>

          <div className="cListTableArea2">
            <ProgressBackdrop isLoading={loading} />
            <GridArea
              gridRef={gridRef}
              columnDefs={LoginListGridDefs.columnDefs}
              rowData={rowData}
              total={total}
              page={page}
              isPaging
              paginationPageSize={10}
              setPage={setPage}
              onGridReady={onFirstDataRendered}
              domLayout="autoHeight"
              defaultColDef={{
                editable: false,
                sortable: true,
                filter: true,
                resizable: true,
              }}
            />
          </div>
          <div className="cPagingArea"></div>
        </div>
      </article>
    </section>
  );
};

export default LoginListPage;
